import { useIsMutating } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';

import { UPDATE_SHIPPING_METHOD_MUTATION_KEY } from '$hooks/mutations';
import { useSetPickUpPoint } from '$hooks/mutations/useSetPickUpPoint';
import { useCart } from '$hooks/queries';
import { usePickupPoints } from '$hooks/queries/usePickupPoints';
import { lastUserPickupPointAtom } from '$src/business/atoms/last-pickup-point';
import { useIsOrderProcessing } from '$src/hooks/client';

/**
 * This hook selects automatically the closer pickup point when a pickup shipping method is selected.
 * Just call this hook at top of the application, it's autonomous
 */
export const useAutoSelectPickupPoint = () => {
  const { mutate } = useSetPickUpPoint();
  const { data: cart } = useCart();
  const [autoSelectDone, setAutoSelectDone] = useState(false);
  // Restore the user's previously-chosen pickup point on segment re-entry
  // (Pickup → Home → Pickup wipes the cart-side selection but the user's
  // intent shouldn't be lost). Falls back to closest (`data[0]`) when the
  // stored point isn't in the fresh results (e.g., address changed) or
  // when the user has never picked one this session. We also WRITE to the
  // atom from the autoselect path so even the auto-picked closest point is
  // remembered — that way the second time the user enters Pickup, the
  // `effectivePickupPoint` fallback in MobileCategoryDetail can paint the
  // chosen point optimistically instead of flashing the spinner during the
  // re-apply round-trip.
  const [lastUserPickupPoint, setLastUserPickupPoint] = useAtom(lastUserPickupPointAtom);

  const isMutatingShippingMethods = useIsMutating({ mutationKey: [UPDATE_SHIPPING_METHOD_MUTATION_KEY] });
  const shippingMethodId = cart?.shipping?.selected?.[0]?.id;
  const isOrderProcessing = useIsOrderProcessing();

  const selectedShippingMethodCarrier = useMemo(
    () =>
      cart?.shipping?.selected?.[0]?.carriers?.[0]?.pickup
        ? cart?.shipping?.selected?.[0]?.carriers?.[0]
        : null,
    [cart?.shipping?.selected],
  );

  const search = useMemo(
    () => ({
      address1: cart?.shipping?.address?.address1 ?? '',
      country: cart?.shipping?.address?.countryCode ?? '',
      zip: cart?.shipping?.address?.zip ?? '',
      city: cart?.shipping?.address?.city ?? '',
    }),
    [cart?.shipping?.address],
  );

  useEffect(() => {
    // if search (and so address) did change, we want to reselect the new first pickupPoint
    setAutoSelectDone(false);
  }, [search, shippingMethodId]);

  const { data, status } = usePickupPoints(selectedShippingMethodCarrier?.pickup, search);

  useEffect(() => {
    if (
      isMutatingShippingMethods === 0 &&
      !isOrderProcessing &&
      cart?.shipping?.selected?.[0]?.id &&
      !cart?.shipping?.selected?.[0]?.pickupPoint &&
      !autoSelectDone &&
      selectedShippingMethodCarrier?.pickup &&
      status !== 'pending' &&
      data?.[0]
    ) {
      // Prefer the user's previous explicit choice (by id) when it still
      // exists in the current results — otherwise default to data[0]
      // (closest). Matches by id only; the search results array is already
      // scoped to the current provider, so cross-carrier collisions can't
      // happen.
      const preferred =
        (lastUserPickupPoint && data.find((p) => p.id === lastUserPickupPoint.id)) ?? data[0];
      // eslint-disable-next-line no-console
      console.log('[AUTOSELECTPIKCUPPOINT] Setting pickup point automatically...', {
        preferred: preferred?.id,
        wasUserChoice: preferred?.id === lastUserPickupPoint?.id,
      });
      setAutoSelectDone(true);
      // Mirror the autoselected point into the atom so subsequent re-entries
      // can render it optimistically (no flash). No-op when the autoselect
      // matches the existing atom value.
      if (preferred && preferred.id !== lastUserPickupPoint?.id) {
        setLastUserPickupPoint(preferred);
      }
      mutate({
        pickupPoint: preferred,
        shippingMethodId: cart.shipping.selected[0].id,
      });
    }
  }, [
    isOrderProcessing,
    autoSelectDone,
    cart?.shipping.selected,
    data,
    isMutatingShippingMethods,
    lastUserPickupPoint,
    mutate,
    selectedShippingMethodCarrier?.pickup,
    setLastUserPickupPoint,
    status,
  ]);

  const pickupPointsStatus = (() => {
    if (!selectedShippingMethodCarrier?.pickup) return 'idle' as const;
    if (status === 'pending' || (!autoSelectDone && data === undefined)) return 'pending' as const;
    if (data?.length === 0) return 'empty' as const;
    if (cart?.shipping?.selected?.[0]?.pickupPoint) return 'done' as const;
    return 'pending' as const;
  })();

  return { pickupPointsStatus };
};
